import React from "react";
import DrawerAppBar from "../../../../../components/mobile/Navbar";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { SeparationState, separationActions } from "../../store";
import {
  NormalizedCargo,
  NormalizedCargoItem,
} from "../../../../../../domain/models/cargo-model";
import Stack from "@mui/material/Stack";
import { useDispatch } from "react-redux";
import { Box, Button, Chip, Divider, Paper, Typography } from "@mui/material";
import { Position } from "../../../../../../domain/models/position-model";
import { LoadProductListByManyCodes } from "../../../../../../domain/usecases/load-product-list-by-many-codes";
import { SeparationStatus } from "../../../../../../domain/models/inventory-reservation";
import { LoadNormalizedCargos } from "../../../../../../domain/usecases/load-normalized-cargos";

type Props = {
  loadCargo: LoadNormalizedCargos;
  loadProductListByManyCodes: LoadProductListByManyCodes;
};

export const ItemsOfCargo: React.FC<Props> = ({
  loadCargo,
  loadProductListByManyCodes,
}) => {
  const navigate = useNavigate();

  const [cargo, setCargo] = React.useState<NormalizedCargo | null>(null);

  const cargoObj = useSelector<SeparationState, NormalizedCargo | null>(
    (state) => state.currentCargo
  );

  if (!cargoObj) {
    navigate("/mobile/separation");
  }

  React.useEffect(() => {
    async function fetch() {
      const warehouseString = localStorage.getItem("currentWarehouse");
      const warehouse = warehouseString
        ? (JSON.parse(warehouseString) as {
            id: number;
            name: string;
          } | null)
        : undefined;
      try {
        const fetchedCargo = await loadCargo.load(warehouse!.id, {
          reference: cargoObj?.reference,
        });

        if (fetchedCargo.length !== 1) {
          alert("Erro ao carregar itens");
          navigate("/mobile/separation/route");
        }

        let codes =
          fetchedCargo[0]?.cargoItems!.map((item) => item.eanCode) || [];

        const products = await loadProductListByManyCodes.load(codes);
        if (!products || products.length === 0) {
          alert(
            "Houve um problema carregando os dados. Entre em contato com o suporte."
          );
          navigate("/mobile/separation/route");
        }

        let updatedCargo = {
          ...fetchedCargo[0],
          cargoItems:
            fetchedCargo[0]?.cargoItems.map((item) => {
              const match = products.find(
                (prod) => prod.eanCode === item.eanCode
              );
              if (match) {
                return {
                  ...item,
                  productDescription: match.description,
                  packageAmount: match.packageAmount,
                };
              }
              return item;
            }) || [],
        };

        setCargo(updatedCargo as NormalizedCargo);
      } catch (error) {
        alert(
          "Houve um problema carregando os dados. Entre em contato com o suporte."
        );
      }
    }

    fetch();
  }, []);

  return (
    <>
      <DrawerAppBar
        title="Separar Mercadoria"
        onBack={() => {
          navigate("/mobile/separation/route");
        }}
      />
      <Stack sx={{ m: "20px" }} spacing={4}>
        <CargoCard cargo={cargo as NormalizedCargo} />
        <Divider />
        {cargo?.cargoItems!.map((item, index) => {
          return <ItemCard item={item} key={index} />;
        })}
      </Stack>
    </>
  );
};

const CargoCard = ({ cargo }: { cargo: NormalizedCargo }) => {
  return (
    <>
      {cargo && (
        <Paper sx={{ p: "20px" }}>
          <Stack direction="row">
            <Box
              sx={{
                width: "60%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Stack>
                <Typography>{`Carga: ${cargo.reference}`}</Typography>
                <Typography fontWeight="bold" fontSize="20px">
                  {cargo.clientName}
                </Typography>
                <Typography>{`Quantidade de Itens: ${cargo.cargoItems.reduce(
                  (acc, item) => {
                    acc += item.inventoryReservations!.length;
                    return acc;
                  },
                  0
                )}`}</Typography>
              </Stack>
            </Box>
            <Box
              sx={{
                width: "40%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-around",
              }}
            ></Box>
          </Stack>
        </Paper>
      )}
    </>
  );
};

const ItemCard = ({ item }: { item: NormalizedCargoItem }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const getPositionString = (position: Position) => {
    return `${position.street}.${position.building}.${position.level}.${position.apartment}`;
  };

  return (
    <>
      {item.inventoryReservations?.map((reservation, index) => {
        console.log(item);
        return (
          <Paper sx={{ p: "10px" }} key={index}>
            <Stack direction="row">
              <Box
                sx={{
                  width: "60%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Stack spacing={2}>
                  <Typography fontWeight="bold" fontSize="20px">
                    {item.productDescription}
                  </Typography>
                  {item.batch ? (
                    <Typography>{`Lote: ${item.batch}`}</Typography>
                  ) : (
                    ""
                  )}
                  <Typography>{`Quantidade: ${(
                    reservation.amount / (item.packageAmount || 1)
                  ).toFixed(2)}`}</Typography>
                  <Typography fontWeight="bold" fontSize="20px">
                    {`Posição: ${getPositionString(
                      reservation.inventory.position
                    )}`}
                  </Typography>
                </Stack>
              </Box>
              <Stack
                sx={{
                  width: "40%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "space-around",
                }}
                spacing={2}
              >
                <Chip
                  label={
                    reservation.status === SeparationStatus.notSeparated
                      ? "Não Iniciada"
                      : "Finalizada"
                  }
                  variant="outlined"
                  color={
                    reservation.status === SeparationStatus.notSeparated
                      ? "error"
                      : "success"
                  }
                  size="small"
                />
                {reservation.status !== SeparationStatus.separated ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{ color: "white" }}
                    onClick={() => {
                      dispatch(
                        separationActions.setCurrentItem({
                          reservation,
                          item,
                        })
                      );
                      navigate("/mobile/separation/position-collector");
                    }}
                  >
                    Iniciar
                  </Button>
                ) : (
                  <Box />
                )}
              </Stack>
            </Stack>
          </Paper>
        );
      })}
    </>
  );
};
